/* eslint-disable */
import { useState } from 'react'
import { Checkbox } from '../../Components/ui/checkbox'
import { Button } from '../../Components/ui/button'
import EmptyState from '../../Components/Common/EmptyState'
import env from '../../env/env'
import { post } from '../../http/restservice'
import { toCurrency } from '../../utils/CurrencyUtil'
import { capitalizeWords, formatIndianNumber } from '../../utils/Util'
import { isDematUser } from '../../utils/AuthUtil'
import { useModal } from '../../hooks/use-modal-store'
import { useErrorPopup } from '../../hooks/useErrorPopup'
import OrderOtpVerification from '../FirstIdeas/OrderOtpVerification'
import { Spinner } from '../../Components/ui/spinner'
import rebalancingEmptyIcon from '../../assets/SVGS/action-center-rebalancing-empty.svg'

const getRedeemAmount = (order) =>
  Number(order?.orderRedemptionAmount ?? order?.orderInvestmentAmount ?? 0)

const getRedeemFunds = (order) =>
  order?.orderRedemptionBreakdown || order?.orderInvestmentBreakdown || []

const getOrderKey = (order, index) =>
  String(order?.id || `${order?.customerBasketInvestmentId}-${index}`)

const buildRedemptionOrderFund = (fund) => {
  const base = {
    isin: fund.isin,
    folioNo: fund.folioNo,
    schemeCode: fund.schemeCode || fund.bseSchemeCode,
    schemeName: fund.schemeName,
    redemptionType: fund.redemptionType,
    nav: fund.nav,
  }

  if (fund.redemptionType === 'ALL_UNITS') {
    return { ...base, isAllRedeem: 'Y' }
  }

  if (fund.redemptionType === 'UNITS') {
    return {
      ...base,
      isAllRedeem: 'N',
      withdrawableUnits: fund.withdrawableUnits,
    }
  }

  return {
    ...base,
    isAllRedeem: 'N',
    withdrawableAmount: fund.withdrawableAmount ?? fund.allocationAmount ?? fund.investmentAmount,
  }
}

const RedeemOrders = ({
  redeemOrders = [],
  clientCode,
  userDetails,
  email,
  onRefresh,
}) => {
  const { onOpen } = useModal()
  const { showError } = useErrorPopup()
  const [selectedOrderKey, setSelectedOrderKey] = useState(null)
  const [openOTPPopup, setOpenOTPPopup] = useState(false)
  const [loading, setLoading] = useState(false)
  const [placingOrder, setPlacingOrder] = useState(false)

  const selectedOrder =
    redeemOrders.find((order, index) => getOrderKey(order, index) === selectedOrderKey) || null

  const handleSelectOrder = (orderKey) => {
    setSelectedOrderKey((current) => (current === orderKey ? null : orderKey))
  }

  const generateOTP = async () => {
    if (!selectedOrder) return

    setLoading(true)
    const response = await post(
      `${env.API_URL}mutualfundservice/api/mutualFund/sendTransactionalOtp`,
      {
        phoneNumber: userDetails?.phoneNumber,
        email: email?.includes('fabits') ? '' : email,
      },
    )
    setLoading(false)

    if (response?.isError) {
      showError('OTP Error', 'Failed to generate OTP. Please try again later.', () => { })
      return
    }

    setOpenOTPPopup(true)
  }

  const placeBasketRedeemOrder = async () => {
    if (!selectedOrder || placingOrder) return

    setPlacingOrder(true)
    try {
      const funds = getRedeemFunds(selectedOrder)
      const orderData = funds.map(buildRedemptionOrderFund)

      const orderPayload = {
        clientCode: clientCode?.toUpperCase(),
        phoneNumber: formatIndianNumber(userDetails?.phoneNumber),
        customerBasketInvestmentId: selectedOrder?.customerBasketInvestmentId,
        orderData,
        subsequentOrderId: selectedOrder?.id,
        isSubsequentOneTimeOrder: true,
      }

      const basketRedeem = await post(
        `${env.API_URL}mutualfundservice/api/basket/oneTimeRedemptionOrder`,
        orderPayload,
      )

      if (basketRedeem?.isError || basketRedeem?.status === 'FAILURE') {
        showError(
          'Redemption Failed!',
          basketRedeem?.message ||
          'There was an issue with redemption. Please try again later. For assistance, contact us at +91-8050034800.',
          () => { },
        )
        return null
      }

      if (basketRedeem?.status === 'SUCCESS') {
        const successOrders = basketRedeem.orders?.filter((o) => o?.status === 'SUCCESS')
        if (!basketRedeem.orders?.length || successOrders?.length === basketRedeem.orders?.length) {
          onOpen('openPopup', {
            title: 'Redemption Initiated!',
            message:
              'Your redemption request has been successfully initiated. Redemption will be completed in 1–5 working days.',
            severity: 'success',
            redirectBackTo: '/dashboard/firstTime/actionPlanUser',
            forwardTo: '/dashboard',
          })
          setSelectedOrderKey(null)
          onRefresh?.()
          return basketRedeem
        }
      }

      showError(
        'Redemption Failed!',
        basketRedeem?.message ||
        'There was an issue with redemption. Please try again later. For assistance, contact us at +91-8050034800.',
        () => { },
      )
      return basketRedeem
    } finally {
      setPlacingOrder(false)
    }
  }

  if (!redeemOrders?.length) {
    return (
      <div className='mx-4 mt-3 pb-48 md:mx-auto md:max-w-[760px] lg:max-w-[1128px]'>
        <div>
          <h1 className='mt-4 flex items-center justify-between'>
            <span className='font-IBM text-xl font-bold text-black-950 md:text-2xl'>
              Redeem Orders
            </span>
          </h1>
          <p className='mt-1 font-DMSans text-sm font-normal text-black-500 md:text-base'>
            Basket redemptions created for you will show up here.
          </p>
        </div>
        <EmptyState
          icon={
            <span
              aria-hidden='true'
              className='fabits-action-center-empty-graphic'
              style={{ '--fabits-empty-graphic-url': `url(${rebalancingEmptyIcon})` }}
            />
          }
          title='No redeem orders'
          description='When a redemption is created for your basket, it will appear in this tab.'
        />
      </div>
    )
  }

  return (
    <div className='mx-4 mt-3 md:mx-auto md:max-w-[760px] lg:max-w-[1128px]'>
      <div>
        <h1 className='mt-4 flex items-center justify-between'>
          <span className='font-IBM text-xl font-bold text-black-950 md:text-2xl'>
            Redeem Orders ({redeemOrders.length})
          </span>
        </h1>
        <p className='mt-1 font-DMSans text-sm font-normal text-black-500 md:text-base'>
          Select one redemption to process
        </p>
      </div>

      <div className='my-4 grid grid-cols-1 gap-3 min-[380px]:grid-cols-2 lg:gap-4'>
        <div className='fabits-card flex-1 p-3 md:p-5'>
          <h1 className='fabits-eyebrow'>Selected redemption</h1>
          <p className='mt-3 flex h-[32px] items-end font-normal text-xl leading-none text-black-950 md:h-[36px] md:text-3xl'>
            <span className='mr-1.5 flex items-end font-DMSans text-lg font-extrabold md:mr-2 md:text-[1.6rem]'>
              ₹
            </span>
            <span className='font-IBM font-bold'>
              {toCurrency(selectedOrder ? getRedeemAmount(selectedOrder) : 0)}
            </span>
          </p>
        </div>

        <div className='fabits-card flex-1 p-3 md:p-5'>
          <h1 className='fabits-eyebrow'>Funds in selection</h1>
          <p className='mt-3 flex h-[32px] items-end font-normal text-xl leading-none text-black-950 md:h-[36px] md:text-3xl'>
            <span className='font-IBM font-bold'>
              {selectedOrder ? getRedeemFunds(selectedOrder).length : 0}
            </span>
          </p>
        </div>
      </div>

      <div className='mb-3 flex items-center justify-between text-sm'>
        <p className='font-medium text-black-700'>Select a redeem order to proceed</p>
      </div>

      <div className='gap-2 pb-56 md:flex md:pb-0'>
        <div className='flex flex-[2] flex-col gap-4 md:grid md:grid-cols-2 md:gap-4 lg:grid-cols-2'>
          {redeemOrders.map((order, index) => {
            const orderKey = getOrderKey(order, index)
            const funds = getRedeemFunds(order)
            const amount = getRedeemAmount(order)
            const isSelected = selectedOrderKey === orderKey
            const status = order?.orderStatus || 'ACTIVE'

            return (
              <div
                key={orderKey}
                className={`fabits-card overflow-hidden p-4 transition-all duration-300 ease-in-out hover:border-primary-200 md:p-5 ${isSelected ? 'border-primary-300' : ''
                  }`}
              >
                <div className='flex justify-between'>
                  <div className='flex items-start gap-3'>
                    <Checkbox
                      className='mt-1 h-5 w-5'
                      checked={isSelected}
                      disabled={placingOrder}
                      onCheckedChange={() => handleSelectOrder(orderKey)}
                    />
                    <div className='font-IBM text-base font-bold leading-snug text-black-950 md:text-lg'>
                      {order?.customerBasketName || 'Basket redemption'}
                    </div>
                  </div>
                </div>

                <div className='mt-4 grid grid-cols-1 gap-3'>
                  <div className='rounded-lg border border-black-100 bg-black-50 p-3'>
                    <h1 className='fabits-eyebrow flex flex-wrap items-center gap-2'>
                      Redemption{' '}
                      <span
                        className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold normal-case tracking-normal ${capitalizeWords(status) === 'Active'
                          ? 'border-secondary-300 bg-secondary-300 text-black-950'
                          : 'border-warning-500 bg-warning-50 text-warning-700'
                          }`}
                      >
                        {capitalizeWords(status) === 'Active' ? 'Active' : status}
                      </span>
                    </h1>
                    <p className='mt-2 font-IBM text-lg font-bold text-black-950 md:text-xl'>
                      ₹{toCurrency(amount)}
                    </p>
                    {funds.length > 0 && (
                      <p
                        className='mt-3 cursor-pointer font-DMSans text-xs font-semibold text-primary-600 hover:text-primary-700'
                        onClick={() => {
                          onOpen('openMFRedeemBreakdown', {
                            constituents: funds,
                            amount: Math.round(amount),
                            onContinue: async () => { },
                          })
                        }}
                      >
                        View Funds
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      <div className='fabits-bottom-sheet fixed bottom-0 left-0 right-0 z-10 mx-auto mt-2 p-4 lg:max-w-[1128px]'>
        <div className='mx-auto space-y-2'>
          {isDematUser() ? (
            <Button
              disabled={!selectedOrder || placingOrder}
              className='w-full bg-primary-600 disabled:bg-primary-300'
              onClick={generateOTP}
              loading={loading || placingOrder}
            >
              Redeem
            </Button>
          ) : (
            <Button
              disabled={!selectedOrder}
              className='w-full bg-primary-600 disabled:bg-primary-300'
              onClick={() =>
                onOpen('openPopup', {
                  title: 'KYC incomplete',
                  message: 'Complete your KYC to redeem.',
                  severity: 'info',
                })
              }
            >
              Complete your KYC
            </Button>
          )}

          <p className='mt-2 text-center text-xs font-semibold text-black-500'>
            You can process one redemption order at a time.
          </p>
        </div>
      </div>

      {openOTPPopup && (
        <OrderOtpVerification
          phoneNumber={userDetails?.phoneNumber}
          openOTPPopup={openOTPPopup}
          setOpenOTPPopup={setOpenOTPPopup}
          email={email}
          handleSipOrOT={placeBasketRedeemOrder}
        />
      )}

      {placingOrder ? (
        <div
          className='fixed inset-0 z-[10002] flex flex-col items-center justify-center gap-3 bg-white/80 backdrop-blur-sm dark:bg-[#040313]/78'
          role='status'
          aria-live='polite'
        >
          <Spinner className='h-8 w-8 text-primary-600' />
          <p className='font-DMSans text-sm font-semibold text-black-700 dark:text-[var(--fb-text)]'>
            Placing your redemption…
          </p>
        </div>
      ) : null}
    </div>
  )
}

export default RedeemOrders
